import { useState, useCallback, useRef } from 'react'
import { SpeedTestResult } from '../components/SpeedTest'

interface SpeedTestState {
  isRunning: boolean
  currentTest: string | null
  progress: number
  results: SpeedTestResult | null
  error: string | null
}

// Smooth speed display using exponential moving average
class SpeedSmoother {
  private displayValue: number = 0
  private alpha: number = 0.3 // Smoothing factor (0.1-0.5, lower = smoother)
  
  update(newValue: number): number {
    if (this.displayValue === 0) {
      this.displayValue = newValue
    } else {
      // Exponential moving average for smooth transitions
      this.displayValue = this.alpha * newValue + (1 - this.alpha) * this.displayValue
    }
    return this.displayValue
  }
  
  reset(): void {
    this.displayValue = 0
  }
  
  getValue(): number {
    return this.displayValue
  }
}

export const useSpeedTest = () => {
  const [state, setState] = useState<SpeedTestState>({
    isRunning: false,
    currentTest: null,
    progress: 0,
    results: null,
    error: null
  })
  
  // Smoothers for download and upload speed display
  const downloadSmoother = useRef(new SpeedSmoother())
  const uploadSmoother = useRef(new SpeedSmoother())

  const updateState = (updates: Partial<SpeedTestState>) => {
    setState(prev => ({ ...prev, ...updates }))
  }

  // Test download speed with adaptive sizing for ~15 seconds
  const testDownloadSpeed = async (): Promise<number> => {
    const TARGET_DURATION = 15000 // 15 seconds in milliseconds
    const MIN_DURATION = 12000 // Minimum 12 seconds
    const MAX_DURATION = 18000 // Maximum 18 seconds
    
    let currentSize = 1024 * 1024 // Start with 1MB
    let speedSamples: number[] = []
    let totalBytes = 0
    let totalTime = 0
    let testStartTime = performance.now()
    let testCount = 0

    updateState({ 
      currentTest: 'download', 
      progress: 10 
    })

    while (performance.now() - testStartTime < MIN_DURATION || 
           (performance.now() - testStartTime < MAX_DURATION && testCount < 8)) {
      
      testCount++
      const progressPercent = Math.min(90, 10 + ((performance.now() - testStartTime) / TARGET_DURATION) * 40)
      updateState({ progress: progressPercent })

      try {
        const startTime = performance.now()
        const response = await fetch(`/api/download?size=${currentSize}`, {
          method: 'GET',
          cache: 'no-cache',
          headers: {
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache'
          }
        })

        if (!response.ok) throw new Error('Download test failed')

        // Stream the response to get more accurate timing
        const reader = response.body?.getReader()
        let receivedBytes = 0
        
        if (reader) {
          while (true) {
            const { done, value } = await reader.read()
            if (done) break
            receivedBytes += value?.length || 0
          }
        } else {
          const data = await response.blob()
          receivedBytes = data.size
        }

        const endTime = performance.now()
        const duration = endTime - startTime
        
        if (duration > 100) { // Only count tests that took more than 100ms
          const speedMbps = (receivedBytes * 8) / (duration * 1000) // Convert to Mbps
          
          if (speedMbps > 0 && speedMbps < 10000) { // Sanity check
            speedSamples.push(speedMbps)
            totalBytes += receivedBytes
            totalTime += duration
            
            // Update real-time display with smoothed download speed
            const smoothedSpeed = downloadSmoother.current.update(speedMbps)
            updateState({ 
              currentTest: 'download',
              progress: progressPercent,
              results: {
                downloadSpeed: smoothedSpeed,
                uploadSpeed: 0,
                ping: 0,
                jitter: 0,
                timestamp: Date.now()
              }
            })
            
            // Adaptive sizing: adjust next test size based on current speed
            if (duration < 2000) { // If test was too fast, increase size
              currentSize = Math.min(currentSize * 2, 10 * 1024 * 1024) // Max 10MB
            } else if (duration > 5000) { // If test was too slow, decrease size
              currentSize = Math.max(currentSize / 2, 512 * 1024) // Min 512KB
            }
          }
        }
      } catch (error) {
        console.error('Download test error:', error)
        // On error, try smaller size
        currentSize = Math.max(currentSize / 2, 512 * 1024)
      }

      // Break if we have enough samples and minimum time
      if (speedSamples.length >= 3 && performance.now() - testStartTime >= MIN_DURATION) {
        break
      }
    }

    if (speedSamples.length === 0) return 0

    // Calculate stable speed by removing outliers and averaging
    speedSamples.sort((a, b) => a - b)
    
    // Remove top and bottom 20% if we have enough samples
    if (speedSamples.length >= 5) {
      const removeCount = Math.floor(speedSamples.length * 0.2)
      speedSamples = speedSamples.slice(removeCount, speedSamples.length - removeCount)
    }

    // Return weighted average (more recent samples have slightly higher weight)
    let weightedSum = 0
    let totalWeight = 0
    
    speedSamples.forEach((speed, index) => {
      const weight = 1 + (index / speedSamples.length) * 0.5 // Recent samples get up to 1.5x weight
      weightedSum += speed * weight
      totalWeight += weight
    })

    return weightedSum / totalWeight
  }

  // Test upload speed with adaptive sizing for stability
  const testUploadSpeed = async (): Promise<number> => {
    const TARGET_DURATION = 10000 // 10 seconds for upload
    const MIN_DURATION = 6000 // Minimum 6 seconds
    const MAX_DURATION = 15000 // Maximum 15 seconds
    
    let currentSize = 256 * 1024 // Start with 256KB (safer size)
    let speedSamples: number[] = []
    let testStartTime = performance.now()
    let testCount = 0

    updateState({ 
      currentTest: 'upload', 
      progress: 50 
    })

    while (performance.now() - testStartTime < MIN_DURATION || 
           (performance.now() - testStartTime < MAX_DURATION && testCount < 6)) {
      
      testCount++
      const progressPercent = Math.min(80, 50 + ((performance.now() - testStartTime) / TARGET_DURATION) * 30)
      updateState({ progress: progressPercent })

      try {
        // Generate random data for upload (crypto.getRandomValues has 64KB limit)
        const data = new Uint8Array(currentSize)
        
        // Fill data in chunks due to crypto.getRandomValues 64KB limit
        const maxChunkSize = 65536 // 64KB limit
        for (let offset = 0; offset < currentSize; offset += maxChunkSize) {
          const chunkSize = Math.min(maxChunkSize, currentSize - offset)
          const chunk = data.subarray(offset, offset + chunkSize)
          crypto.getRandomValues(chunk)
        }

        const startTime = performance.now()
        
        // Create a more realistic upload stream
        const response = await fetch('/api/upload', {
          method: 'POST',
          body: data,
          headers: {
            'Content-Type': 'application/octet-stream',
            'Content-Length': currentSize.toString(),
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache'
          }
        })

        if (!response.ok) {
          const errorText = await response.text()
          throw new Error(`Upload test failed: ${response.status} - ${errorText}`)
        }

        const result = await response.json()
        const endTime = performance.now()
        
        const duration = endTime - startTime
        
        const speedMbps = (currentSize * 8) / (duration * 1000) // Convert to Mbps
        console.log(`Upload test ${testCount}: ${currentSize} bytes in ${duration}ms = ${speedMbps.toFixed(2)} Mbps, Success: ${result.success}`)
        
        // More lenient conditions for counting upload tests
        if (duration > 100 && result.success && speedMbps > 0.1) { // Accept tests > 100ms with reasonable speed
          if (speedMbps < 10000) { // Sanity check for maximum speed
            speedSamples.push(speedMbps)
            
            // Update real-time display with smoothed upload speed
            const smoothedSpeed = uploadSmoother.current.update(speedMbps)
            updateState({ 
              currentTest: 'upload',
              progress: progressPercent,
              results: {
                downloadSpeed: 0,
                uploadSpeed: smoothedSpeed,
                ping: 0,
                jitter: 0,
                timestamp: Date.now()
              }
            })
            
            // Conservative adaptive sizing for upload
            if (duration < 800) { // If test was too fast, increase size
              currentSize = Math.min(currentSize * 1.6, 3 * 1024 * 1024) // Max 3MB
            } else if (duration > 2500) { // If test was too slow, decrease size
              currentSize = Math.max(currentSize / 1.4, 64 * 1024) // Min 64KB
            } else if (duration < 1500) { // Moderate increase
              currentSize = Math.min(currentSize * 1.15, 3 * 1024 * 1024)
            }
          }
        } else {
          console.log(`Upload test ${testCount} rejected: duration=${duration}ms, success=${result.success}, speed=${speedMbps.toFixed(2)}Mbps`)
        }
      } catch (error) {
        console.error('Upload test error:', error)
        // On error, try smaller size
        currentSize = Math.max(currentSize / 2, 64 * 1024)
      }

      // Break if we have enough samples and minimum time
      if (speedSamples.length >= 3 && performance.now() - testStartTime >= MIN_DURATION) {
        break
      }
    }

    console.log(`Upload test completed with ${speedSamples.length} valid samples:`, speedSamples)
    
    if (speedSamples.length === 0) {
      console.log('No valid upload samples, returning 0')
      return 0
    }

    // Calculate stable speed by removing outliers
    speedSamples.sort((a, b) => a - b)
    
    // Remove top and bottom 20% for upload (less aggressive filtering)
    if (speedSamples.length >= 5) {
      const removeCount = Math.floor(speedSamples.length * 0.2)
      speedSamples = speedSamples.slice(removeCount, speedSamples.length - removeCount)
      console.log(`After removing outliers, ${speedSamples.length} samples remain:`, speedSamples)
    }

    // Return median of remaining samples for stability
    let finalSpeed = 0
    if (speedSamples.length === 1) {
      finalSpeed = speedSamples[0]
    } else if (speedSamples.length === 2) {
      finalSpeed = (speedSamples[0] + speedSamples[1]) / 2
    } else {
      const midIndex = Math.floor(speedSamples.length / 2)
      if (speedSamples.length % 2 === 0) {
        finalSpeed = (speedSamples[midIndex - 1] + speedSamples[midIndex]) / 2
      } else {
        finalSpeed = speedSamples[midIndex]
      }
    }
    
    console.log(`Final upload speed: ${finalSpeed.toFixed(2)} Mbps`)
    return finalSpeed
  }

  // Test ping/latency
  const testPing = async (): Promise<{ ping: number; jitter: number }> => {
    const pingTests = 5
    const pings: number[] = []

    updateState({ currentTest: 'ping', progress: 80 })

    for (let i = 0; i < pingTests; i++) {
      try {
        const startTime = performance.now()
        const response = await fetch('/api/ping', {
          method: 'GET',
          cache: 'no-cache'
        })
        const endTime = performance.now()

        if (response.ok) {
          const ping = endTime - startTime
          pings.push(ping)
        }
      } catch (error) {
        console.error('Ping test error:', error)
      }

      updateState({ progress: 80 + ((i + 1) / pingTests) * 15 })
    }

    if (pings.length === 0) {
      return { ping: 0, jitter: 0 }
    }

    const avgPing = pings.reduce((sum, ping) => sum + ping, 0) / pings.length
    const jitter = pings.length > 1 ? 
      Math.sqrt(pings.reduce((sum, ping) => sum + Math.pow(ping - avgPing, 2), 0) / pings.length) : 0

    return { ping: avgPing, jitter }
  }

  // Main speed test function - sequential testing for accuracy
  const startTest = useCallback(async () => {
    // Reset smoothers for new test
    downloadSmoother.current.reset()
    uploadSmoother.current.reset()
    
    updateState({
      isRunning: true,
      currentTest: 'preparing',
      progress: 0,
      results: null,
      error: null
    })

    try {
      // Initial preparation
      updateState({ currentTest: 'preparing', progress: 5 })
      await new Promise(resolve => setTimeout(resolve, 1000)) // Allow UI to update

      // Test download speed first (15 seconds target)
      console.log('Starting download speed test...')
      const downloadSpeed = await testDownloadSpeed()
      console.log(`Download speed completed: ${downloadSpeed.toFixed(2)} Mbps`)
      
      // Brief pause between tests
      updateState({ currentTest: 'preparing', progress: 50 })
      await new Promise(resolve => setTimeout(resolve, 500))
      
      // Test upload speed second (12 seconds target)
      console.log('Starting upload speed test...')
      const uploadSpeed = await testUploadSpeed()
      console.log(`Upload speed completed: ${uploadSpeed.toFixed(2)} Mbps`)
      
      // Test ping/latency
      console.log('Starting ping test...')
      const { ping, jitter } = await testPing()
      console.log(`Ping test completed: ${ping.toFixed(1)}ms, Jitter: ${jitter.toFixed(1)}ms`)

      updateState({ currentTest: 'analyzing', progress: 95 })

      // Final analysis
      await new Promise(resolve => setTimeout(resolve, 1000))

      const results: SpeedTestResult = {
        downloadSpeed,
        uploadSpeed,
        ping,
        jitter,
        timestamp: Date.now()
      }

      console.log('Speed test completed:', results)

      updateState({
        isRunning: false,
        currentTest: null,
        progress: 100,
        results,
        error: null
      })

      // Store results in localStorage for history
      const history = JSON.parse(localStorage.getItem('speedtest-history') || '[]')
      history.unshift(results)
      // Keep only last 10 results
      if (history.length > 10) {
        history.splice(10)
      }
      localStorage.setItem('speedtest-history', JSON.stringify(history))

    } catch (error) {
      console.error('Speed test error:', error)
      updateState({
        isRunning: false,
        currentTest: null,
        progress: 0,
        error: error instanceof Error ? error.message : 'Test failed'
      })
    }
  }, [])

  const resetTest = useCallback(() => {
    setState({
      isRunning: false,
      currentTest: null,
      progress: 0,
      results: null,
      error: null
    })
  }, [])

  return {
    ...state,
    startTest,
    resetTest
  }
}
